<template>
    <div class="animated fadeIn transaksi-pembayaran-page">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <div v-else>
            <!-- Top Card: Transaksi Pembayaran Baru -->
            <div class="transaksi-pembayaran-card mb-4">
                <div class="card-header-row d-flex align-items-center justify-content-between mb-4">
                    <h4 class="page-title mb-0">Transaksi Pembayaran</h4>
                    <span class="card-subtitle-tag">TRANSAKSI PEMBAYARAN BARU</span>
                </div>

                <div class="table-responsive-wrapper">
                    <b-table 
                        hover 
                        show-empty 
                        :busy="isBusy" 
                        :items="daftarPembayaran"
                        :no-local-sorting="false" 
                        :fields="fields2"
                        @sort-changed="sortingChanged" 
                        responsive 
                        class="custom-data-table mb-0"
                    >
                        <template v-slot:table-busy>
                            <div class="text-center text-muted my-3 py-2">
                                <b-spinner class="align-middle mr-2" variant="primary" small></b-spinner>
                                <span>Loading data...</span>
                            </div>
                        </template>
                        <template v-slot:empty>
                            <div class="text-center py-4 text-muted">
                                Tidak ada data daftar pembayaran baru.
                            </div>
                        </template>
                        <template v-slot:cell(noKwitansi)="row">
                            <p class="mb-0 text-faktur">{{ row.item.noKwitansi }}</p>
                            <p class="mb-0 item-secondary-text">
                                {{ row.item.noFaktur }}
                                <b-badge variant="warning" class="ml-1" v-if="row.item.isRollover || (row.item.counterPerpanjangan && row.item.counterPerpanjangan >= 4)" style="font-size: 10px;">
                                    Rollover
                                </b-badge>
                                <b-badge variant="info" class="ml-1" v-else-if="row.item.counterPerpanjangan && row.item.counterPerpanjangan > 0" style="font-size: 10px;">
                                    Ke-{{ row.item.counterPerpanjangan }}
                                </b-badge>
                            </p>
                        </template>
                        <template v-slot:cell(namaAnggota)="row">
                            <p class="mb-0 item-primary-text">{{ row.item.namaAnggota }}</p>
                            <p class="mb-0 item-secondary-text">{{ row.item.noAnggota }}</p>
                        </template>
                        <template v-slot:cell(barang)="row">
                            <p class="mb-0 item-primary-text">{{ row.item.jenisBarang }}</p>
                            <p class="mb-0 item-secondary-text">{{ row.item.merkBarang }} - {{ row.item.tipeBarang }}</p>
                        </template>
                        <template v-slot:cell(jenis)="row">
                            <p class="mb-0 item-primary-text">{{ row.item.jenis == 'Perpanjangan + Cicilan Pokok' ? 'Perpanjangan' : row.item.jenis }}</p>
                            <p class="mb-0 item-secondary-text">{{ row.item.metodePembayaran || (row.item.pembayaranTunai !== undefined ? (row.item.pembayaranTunai ? 'Tunai' : 'Transfer') : '') }}</p>
                        </template>
                        <template v-slot:cell(nominalPembayaran)="row">
                            <div class="text-right item-primary-text" v-if="row.item.nominalPembayaran == null">
                                {{ $helper.addThousandSeparator(row.item.nominalPembayaran) }}
                            </div>
                            <div class="text-right item-primary-text" v-else>
                                Rp. {{ $helper.addThousandSeparator(row.item.nominalPembayaran) }}
                            </div>
                        </template>
                        <template v-slot:cell(status)="row">
                            <span class="badge-status-pill badge-status-terbayar" v-if="row.item.status == 'Terbayar' || row.item.status == 'TERBAYAR'">TERBAYAR</span>
                            <span class="badge-status-pill badge-status-baru" v-else-if="row.item.status == 'Baru' || row.item.status == 'BARU'">BARU</span>
                            <span class="badge-status-pill badge-status-req-void" v-else-if="row.item.status == 'Request Void' || row.item.status == 'REQUEST VOID' || row.item.status == 'REQ. VOID'">REQ. VOID</span>
                            <span class="badge-status-pill badge-status-void" v-else-if="row.item.status == 'Void' || row.item.status == 'VOID'">VOID</span>
                            <span class="badge-status-pill badge-status-neutral" v-else>{{ row.item.status }}</span>
                        </template>
                        <template v-slot:cell(counterPrint)="row">
                            <span class="text-print-count">{{ row.item.counterPrint != null ? row.item.counterPrint : (row.item.cetakKwitansi || 0) }}</span>
                        </template>
                        <template v-slot:cell(action)="row">
                            <div class="text-center">
                                <b-link :to="`${$route.path}/${row.item.id}`" class="action-btn-info" title="Detail" name="daftarPembayaranViewBtn">
                                    <i class="fa fa-info"></i>
                                </b-link>
                            </div>
                        </template>
                    </b-table>
                </div>
            </div>

            <!-- Bottom Card: History Transaksi Pembayaran Hari Ini -->
            <div class="transaksi-pembayaran-card">
                <div class="card-header-row mb-4">
                    <span class="card-section-title">HISTORY TRANSAKSI PEMBAYARAN HARI INI</span>
                </div>

                <!-- Filter Section -->
                <div class="filter-section mb-4">
                    <div class="filter-eyebrow mb-2">FILTER</div>
                    <div class="filter-controls-row">
                        <b-form-input 
                            v-model="input.filterNoFaktur" 
                            name="filterNoFaktur"
                            id="filterInput"
                            type="search"
                            placeholder="No. Kwitansi / No. Faktur"
                            class="filter-input input-pembayaran"
                            @keyup.enter="onFilter"
                        ></b-form-input>
                        <button 
                            type="button" 
                            class="btn btn-filter-search" 
                            :disabled="isBusy2" 
                            @click="onFilter" 
                            name="searchTableBtn"
                        > 
                            <i class="fa fa-search"></i>
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-filter-reset" 
                            :disabled="isBusy2" 
                            @click="onReset" 
                            name="reseFilterBtn"
                        >
                            Reset
                        </button>
                    </div>
                </div>

                <!-- Data Table -->
                <div class="table-responsive-wrapper">
                    <b-table 
                        hover 
                        show-empty 
                        :busy="isBusy2" 
                        :items="daftarPembayaranHistory"
                        :no-local-sorting="false" 
                        :fields="fields2" 
                        @sort-changed="sortingChanged" 
                        responsive 
                        class="custom-data-table mb-0"
                    >
                        <template v-slot:table-busy>
                            <div class="text-center text-muted my-3 py-2">
                                <b-spinner class="align-middle mr-2" variant="primary" small></b-spinner>
                                <span>Loading data...</span>
                            </div>
                        </template>
                        <template v-slot:empty>
                            <div class="text-center py-4 text-muted">
                                Tidak ada data history transaksi pembayaran.
                            </div>
                        </template>

                        <!-- Slot: NO. KWITANSI & FAKTUR -->
                        <template v-slot:cell(noKwitansi)="row">
                            <p class="mb-0 text-faktur">{{ row.item.noKwitansi }}</p>
                            <p class="mb-0 item-secondary-text">
                                {{ row.item.noFaktur }}
                                <b-badge variant="warning" class="ml-1" v-if="row.item.isRollover || (row.item.counterPerpanjangan && row.item.counterPerpanjangan >= 4)" style="font-size: 10px;">
                                    Rollover
                                </b-badge>
                                <b-badge variant="info" class="ml-1" v-else-if="row.item.counterPerpanjangan && row.item.counterPerpanjangan > 0" style="font-size: 10px;">
                                    Ke-{{ row.item.counterPerpanjangan }}
                                </b-badge>
                            </p>
                        </template>

                        <!-- Slot: ANGGOTA -->
                        <template v-slot:cell(namaAnggota)="row">
                            <p class="mb-0 item-primary-text">{{ row.item.namaAnggota }}</p>
                            <p class="mb-0 item-secondary-text">{{ row.item.noAnggota }}</p>
                        </template>

                        <!-- Slot: BARANG -->
                        <template v-slot:cell(barang)="row">
                            <p class="mb-0 item-primary-text">{{ row.item.jenisBarang }}</p>
                            <p class="mb-0 item-secondary-text">{{ row.item.merkBarang }} - {{ row.item.tipeBarang }}</p>
                        </template>

                        <!-- Slot: JENIS PEMBAYARAN -->
                        <template v-slot:cell(jenis)="row">
                            <p class="mb-0 item-primary-text">{{ row.item.jenis == 'Perpanjangan + Cicilan Pokok' ? 'Perpanjangan' : row.item.jenis }}</p>
                            <p class="mb-0 item-secondary-text">{{ row.item.metodePembayaran || (row.item.pembayaranTunai !== undefined ? (row.item.pembayaranTunai ? 'Tunai' : 'Transfer') : '') }}</p>
                        </template>

                        <!-- Slot: TOTAL PEMBAYARAN -->
                        <template v-slot:cell(nominalPembayaran)="row">
                            <div class="text-right item-primary-text" v-if="row.item.nominalPembayaran == null">
                                {{ $helper.addThousandSeparator(row.item.nominalPembayaran) }}
                            </div>
                            <div class="text-right item-primary-text" v-else>
                                Rp. {{ $helper.addThousandSeparator(row.item.nominalPembayaran) }}
                            </div>
                        </template>

                        <!-- Slot: STATUS -->
                        <template v-slot:cell(status)="row">
                            <span class="badge-status-pill badge-status-terbayar" v-if="row.item.status == 'Terbayar' || row.item.status == 'TERBAYAR'">TERBAYAR</span>
                            <span class="badge-status-pill badge-status-baru" v-else-if="row.item.status == 'Baru' || row.item.status == 'BARU'">BARU</span>
                            <span class="badge-status-pill badge-status-req-void" v-else-if="row.item.status == 'Request Void' || row.item.status == 'REQUEST VOID' || row.item.status == 'REQ. VOID'">REQ. VOID</span>
                            <span class="badge-status-pill badge-status-void" v-else-if="row.item.status == 'Void' || row.item.status == 'VOID'">VOID</span>
                            <span class="badge-status-pill badge-status-neutral" v-else>{{ row.item.status }}</span>
                        </template>

                        <!-- Slot: # PRINT -->
                        <template v-slot:cell(counterPrint)="row">
                            <span class="text-print-count">{{ row.item.counterPrint != null ? row.item.counterPrint : (row.item.cetakKwitansi || 0) }}</span>
                        </template>

                        <!-- Slot: AKSI -->
                        <template v-slot:cell(action)="row">
                            <div class="text-center">
                                <b-link :to="`${$route.path}/${row.item.id}`" class="action-btn-info" title="Detail" name="daftarPembayaranHistoryViewBtn">
                                    <i class="fa fa-info"></i>
                                </b-link>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- Footer Pagination -->
                <div class="table-pagination-footer d-flex align-items-center justify-content-between mt-4 pt-2">
                    <div class="limit-wrapper d-flex align-items-center">
                        <span class="limit-label mr-2">Tampilkan</span>
                        <b-form-select 
                            v-model="input.show" 
                            :options="options" 
                            @change="onLimitChange" 
                            class="limit-select"
                        >
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled />
                            </template>
                        </b-form-select>
                        <span class="entries-info ml-3">{{ totalViewText }}</span>
                    </div>
                    <b-pagination 
                        class="custom-pagination mb-0"
                        v-model="currentPage"
                        :total-rows="rows"
                        :per-page="perPage"
                        @input="onPageChange"
                        aria-controls="my-table"
                        prev-text="‹"
                        next-text="›"
                        hide-goto-end-buttons
                    ></b-pagination>
                </div>
            </div>
        </div>

        <b-modal id="modal-info-status" title="Info Status" no-close-on-backdrop>
            <div class="detail-data-wrapper mb-3">
                <h6 class="form-title mb-1">BARU</h6>
                <p class="smallText mb-0">Transaksi pembayaran yang baru diinput dan belum ditransaksikan. Seluruh pembayaran BARU yang tidak dieksekusi, akan dihapus keesokan harinya.</p>
            </div>
            <div class="detail-data-wrapper mb-3">
                <h6 class="form-title mb-1">TERBAYAR</h6>
                <p class="smallText mb-0">Transaksi pembayaran sudah dieksekusi (kwitansi sudah dicetak, kas cabang sudah berubah).</p>
            </div>
            <div class="detail-data-wrapper mb-3">
                <h6 class="form-title mb-1">VOID</h6>
                <p class="smallText mb-0">Transaksi pembayaran sudah digugurkan (VOID).</p>
            </div>
            <div class="detail-data-wrapper mb-0">
                <h6 class="form-title mb-1">REQUEST VOID</h6>
                <p class="smallText mb-0">Transaksi pembayaran sedang dalam proses VOID.</p>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button variant="secondary" name="closeDetailBtn" @click="cancel()">
                    Tutup
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'transaksi-pembayaran',
    data(){
        return{
            initStatus: 0,
            isBusy: true,
            isBusy2: true,
            input:{
                show: 10,
                sortDesc: null,
                sortBy: null,
                sortDesc: null,
                filter: null,
                filterNoFaktur: null,
                filterNoAnggota: null,
            },
            fields: [
                { key: "noKwitansi", label: "NO. KWITANSI & FAKTUR", class: "align-middle" },
                { key: "namaAnggota", label: "ANGGOTA", class: "align-middle" },
                { key: "barang", label: "BARANG", class: "align-middle" },
                { key: "jenis", label: "JENIS PEMBAYARAN", class: "align-middle" },
                { key: "nominalPembayaran", label: "TOTAL PEMBAYARAN", class: "align-middle text-right" },
                { key: "status", label: "STATUS", class: "align-middle text-center" },
                { key: "counterPrint", label: "# PRINT", class: "align-middle text-center" },
                { key: "action", label: "AKSI", class: "align-middle text-center" }
            ],
            fields2: [
                { key: "noKwitansi", label: "NO. KWITANSI & FAKTUR", class: "align-middle" },
                { key: "namaAnggota", label: "ANGGOTA", class: "align-middle" },
                { key: "barang", label: "BARANG", class: "align-middle" },
                { key: "jenis", label: "JENIS PEMBAYARAN", class: "align-middle" },
                { key: "nominalPembayaran", label: "TOTAL PEMBAYARAN", class: "align-middle text-right" },
                { key: "status", label: "STATUS", class: "align-middle text-center" },
                { key: "counterPrint", label: "# PRINT", class: "align-middle text-center" },
                { key: "action", label: "AKSI", class: "align-middle text-center" }
            ],
            daftarPembayaran: [],
            daftarPembayaranHistory: [],
            options: [
                { value: 10, text: '10'},
                { value: 20, text: '20'},
                { value: 30, text: '30'},
                { value: 40, text: '40'},
                { value: 50, text: '50'},
            ],
            currentPage: 1,
            perPage: 10,
            rows: null,
            totalViewText: "",
            page: '',
            size: '',
            srcCabangId: null,
            loading: false,
            query: "",
            query2: ""
        }
    }, 
    methods: {

        //----------------- Fungsi Inisialisasi -----------------

        init(){
            this.initStatus = 0
            this.srcCabangId = this.$helper.getCabangId()
            this.page = 0
            this.size = this.input.show
            this.query = this.$helper.makeQuery(0, 100)
            this.query2 = this.$helper.makeQuery(this.page, this.size)

            let getDaftarPembayaran = this.$axios.get("gadai-pembayaran/"+this.query+"&cabangId="+this.srcCabangId+"&filterStatus=0").then(response => {
                this.daftarPembayaran= response.data.data
            }) 

            let getDaftarPembayaranHistory = this.$axios.get("gadai-pembayaran/"+this.query2+"&cabangId="+this.srcCabangId+"&filterStatus=-1").then(response => {
                this.daftarPembayaranHistory = response.data.data
                this.updateTotalItem(response.data.total)
            })

            Promise.all([getDaftarPembayaran, getDaftarPembayaranHistory]).then(() => {
                this.initStatus = 1
                this.toggleBusy()
                this.toggleBusy2()

                let msg = this.$helper.getFlash()
                this.$helper.toastSucc(this, msg)
            })
            .catch(error => {
                console.log(error)
                this.initStatus = -1
            })
        },


        //----------------- Fungsi Ambil Data Daftar Pembayaran  -----------------

        getDaftarPembayaranHistory(){
            this.toggleBusy2()
            this.daftarPembayaranHistory = []

            if (this.input.filterNoFaktur != null) this.query2 = this.query2 + "&filterNoFaktur=" + this.input.filterNoFaktur
            if (this.input.sortBy != null) this.query2 = this.query2 + "&sortBy=" + this.input.sortBy
            if (this.input.sortDesc != null) this.query2 = this.query2 + "&sortDesc=" + this.input.sortDesc
            
            this.$axios.get("gadai-pembayaran/"+this.query2+"&cabangId="+this.srcCabangId+"&filterStatus=-1").then(response => {
                this.daftarPembayaranHistory = response.data.data
                this.toggleBusy2()
                this.updateTotalItem(response.data.total)
                this.loading = false
            })
            .catch(error => {
                console.log(error)
                console.log(error.response.data.reason)
                this.$helper.parseError(this, error)
                this.toggleBusy2()
                this.loading = false
            })
        },

        //----------------- Fungsi Pagination, Sorting, dan Filtering  -----------------

        onLimitChange(){
            this.size = this.input.show
            this.perPage = this.input.show
            this.currentPage = 1
            this.page = 0
            this.query2 = this.$helper.makeQuery(0, this.size)
            this.getDaftarPembayaranHistory()
        },

        onPageChange(){
            if (this.loading == false){
                this.page = this.currentPage - 1
                this.query2 = this.$helper.makeQuery(this.page, this.size)
                this.getDaftarPembayaranHistory()
            }
        },

        sortingChanged(ctx) {
            if(ctx.sortBy != ""){
                this.input.sortBy = ctx.sortBy
                this.input.sortDesc = ctx.sortDesc
                this.onFilter()
            }
        },

        onReset(){
            this.input.filterNoFaktur = null
            this.currentPage = 1
            this.page = 0
            this.query2 = this.$helper.makeQuery(0, this.size)
            this.loading = true
            this.getDaftarPembayaranHistory()
        },

        onFilter(){
            this.page = 0
            this.currentPage = 1
            this.query2 = this.$helper.makeQuery(0, this.size)
            this.loading = true
            this.getDaftarPembayaranHistory()
        },


        //----------------- Fungsi Lain -----------------

        toggleBusy() {
            if(this.isBusy == true) this.isBusy = false
            else this.isBusy = true
        },

        toggleBusy2() {
            if(this.isBusy2 == true) this.isBusy2 = false
            else this.isBusy2 = true
        },

        updateTotalItem(total){
            let limit = null
            let prevLimit = null
            this.perPage = this.size
            this.rows = total
            prevLimit = this.page * this.size + 1
            limit = this.page * this.size + this.size
            if (limit > this.rows) limit = this.rows
            this.totalViewText = "Menampilkan "+ prevLimit + " - " + limit + " dari " + this.rows + " entri"
        },

        updateTotalItem(total){
            let limit = null
            let prevLimit = null
            this.perPage = this.size

            this.rows = total
            prevLimit = this.page * this.size + 1
            limit = this.page * this.size + this.size
            if (limit > this.rows) limit = this.rows
            this.totalViewText = "Menampilkan "+ prevLimit + " - " + limit + " dari " + this.rows + " entri"
        },

    },
    created(){
        this.init()
    }
}
</script>
